import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
} from '@mui/material';
import React, { useMemo, useCallback } from 'react';
import { useEffect, useState } from 'react';
import { useDebounce } from '@/hooks/useDebounce';

import { ADMINS_DIT_PATH } from '../../../../utils/constants';
import { AdminsPageShell } from '../../../../components/AdminsPageShell';
import FileDropZone from '../../../../components/FileDropZone';
import { styles } from './styles';
import { useDitGetCountries } from '../../hooks/useDitGetCountries';
import { useDitGetCountryEvents } from '../../hooks/useDitGetCountryEvents';
import { useDitIngestionFlow } from '../../hooks/useDitIngestionFlow';
import { useHistory } from 'react-router-dom';
import { useSnackBarContext } from '../../../../contexts/SnackbarContext';

const LoadingState = () => (
  <Box sx={styles.loadingContainer}>
    <Skeleton variant="rectangular" width="100%" height={40} />
    <Skeleton variant="rectangular" width="100%" height={40} />
    <Skeleton variant="rectangular" width="100%" height={150} />
    <Skeleton variant="rectangular" width="100%" height={100} />
    <Skeleton variant="rectangular" width="20%" height={40} />
  </Box>
);

const ErrorState = ({ error }) => (
  <Box
    sx={{
      padding: 2,
      border: '1px solid #f44336',
      borderRadius: 4,
      backgroundColor: '#ffebee',
      color: '#f44336',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 2,
    }}
  >
    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
      Something went wrong
    </Typography>
    <Typography variant="body1">{error.message}</Typography>
    <Button variant="outlined" color="error" onClick={() => window.location.reload()}>
      Retry
    </Button>
  </Box>
);

const EventTable = ({ country, events, onFilterChange, loading }) => {
  const DEFAULT_FILTERS = { sourceType: '', sourceFiles: '', status: '' };
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const debouncedFilters = useDebounce(filters, 300);

  // reset filters when country changes
  useEffect(() => {
    setFilters(DEFAULT_FILTERS);
  }, [country?.isoCode]);

  useEffect(() => {
    onFilterChange?.({ filters: debouncedFilters });
  }, [debouncedFilters]);

  const renderFilterInput = (label, fieldKey) => (
    <TextField
      key={fieldKey}
      label={label}
      value={filters[fieldKey]}
      onChange={(e) => setFilters({ ...filters, [fieldKey]: e.target.value })}
      size="small"
    />
  );

  return (
    <>
      <Box sx={styles.filterBoxContainer}>
        {renderFilterInput('Source', 'sourceType')}
        {renderFilterInput('File', 'sourceFiles')}
        {renderFilterInput('Status', 'status')}
      </Box>
      <TableContainer component={Paper} sx={styles.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={styles.tableHeader}>ID</TableCell>
              <TableCell sx={styles.tableHeader}>Source</TableCell>
              <TableCell sx={styles.tableHeader}>File</TableCell>
              <TableCell sx={styles.tableHeader}>Message</TableCell>
              <TableCell sx={styles.tableHeader}>Status</TableCell>
              <TableCell sx={styles.tableHeader}>Last Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : (
              events.map((event) => {
                const { sourceTypeId } = event;
                const source = country.sources.find((s) => s.sourceId === sourceTypeId);
                const statusColor =
                  styles.statusColors[event.status] || styles.statusColors.DEFAULT;

                return (
                  <TableRow
                    key={event.id}
                    sx={styles.tableRow}
                    component="a"
                    href={`${ADMINS_DIT_PATH}/${event.id}`}
                  >
                    <TableCell style={styles.idLabel}>{event.id.split('-')[0]}</TableCell>
                    <TableCell>{source?.name}</TableCell>
                    <TableCell sx={styles.tableCellFilename}>
                      {event.sourceFiles?.map((f) => f.filename).join(', ') || ''}
                    </TableCell>
                    <TableCell style={styles.messageLabel}>{event.message}</TableCell>
                    <TableCell
                      style={{
                        ...styles.statusLabel,
                        color: statusColor,
                      }}
                    >
                      {event.status}
                    </TableCell>
                    <TableCell>{new Date(event.updatedAt).toLocaleString()}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

const SourceDetails = ({ country, source }) => {
  const { setSnack } = useSnackBarContext();
  const history = useHistory();
  const [selectedFiles, setSelectedFiles] = useState({}); // { tag1: File, tag2: File }
  const { runIngestionFlow, loading, error } = useDitIngestionFlow();
  const allFilesSelected = useMemo(() => {
    return Object.keys(source.fileSpecs).every((tag) => selectedFiles[tag]);
  }, [selectedFiles, source.fileSpecs]);

  const handleStartDataIngestion = async () => {
    const createEventVars = {
      countryIsoCode: country.isoCode,
      sourceId: source.sourceId,
      sourceFiles: selectedFiles,
    };

    const result = await runIngestionFlow(createEventVars);

    if (result.success) {
      history.push(`${ADMINS_DIT_PATH}/${result.eventId}`);
    } else {
      setSnack({
        open: true,
        message: `Error during data ingestion: ${result.error.message}`,
        severity: 'error',
      });

      if (result.abortError) {
        setSnack({
          open: true,
          message: `Error aborting event: ${result.abortError.message}`,
          severity: 'error',
        });
      }
    }
  };

  return (
    <div style={{ marginTop: '16px' }}>
      <div style={styles.sourceSection}>
        <Typography style={styles.sourceUrlLabel}>Source URL:</Typography>
        <a href={source.url} target="_blank" rel="noopener noreferrer" style={styles.sourceUrlLink}>
          {source.url}
        </a>
      </div>
      <div dangerouslySetInnerHTML={{ __html: source.description }} style={styles.sourceSection} />
      <style>{styles.preStyle}</style>
      <Box sx={styles.dropZoneContainer}>
        {Object.entries(source.fileSpecs).map(([tag, extensions]) => (
          <Box key={tag} sx={styles.dropZoneBox}>
            <FileDropZone
              tag={tag}
              onFileSelect={(file) => setSelectedFiles((prev) => ({ ...prev, [tag]: file }))}
              allowedExtensions={extensions}
            />
          </Box>
        ))}
      </Box>
      <div style={styles.submitContainer}>
        <button
          onClick={handleStartDataIngestion}
          style={{
            ...styles.submitButton,
            opacity: allFilesSelected && !loading ? 1 : 0.5,
            cursor: allFilesSelected && !loading ? 'pointer' : 'not-allowed',
          }}
          disabled={!allFilesSelected || loading}
        >
          Start Data Ingestion
        </button>
        {loading && <CircularProgress size={24} style={styles.spinner} />}
      </div>
      {error && (
        <div style={styles.errorContainer}>
          <p style={styles.errorText}>{error.message}</p>
        </div>
      )}
    </div>
  );
};

export const DitDashboard = () => {
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedSource, setSelectedSource] = useState('');
  const [events, setEvents] = useState([]);

  const { countries, loading: loadingCountries, error: getCountriesError } = useDitGetCountries();
  const {
    getDitCountryEvents,
    loading: loadingCountryEvents,
    error: getCountryEventsError,
  } = useDitGetCountryEvents();

  const error = getCountriesError || getCountryEventsError;

  const runningEvent = useMemo(() => {
    return events.find((event) => event.status === 'RUNNING');
  }, [events]);

  // Fetch events when a country is selected
  useEffect(() => {
    if (!selectedCountry) return;
    getDitCountryEvents({ countryIsoCode: selectedCountry.isoCode }).then((res) => {
      setEvents(res?.data?.ditCountryEvents || []);
    });
  }, [selectedCountry]);

  const onCountryChange = (event) => {
    const country = countries.find((c) => c.isoCode === event.target.value);
    setSelectedCountry(country);
    setSelectedSource('');
  };

  const onSourceChange = (event) => {
    const sourceId = event.target.value;
    const source = selectedCountry.sources.find((s) => s.sourceId === sourceId);
    setSelectedSource(source);
  };

  const handleFilterChange = useCallback(
    async ({ filters }) => {
      const result = await getDitCountryEvents({
        countryIsoCode: selectedCountry.isoCode,
        sourceFiles: filters.sourceFiles,
        sourceType: filters.sourceType,
        status: filters.status,
        limit: 100,
      });
      setEvents(result?.data?.ditCountryEvents || []);
    },
    [selectedCountry?.isoCode, getDitCountryEvents],
  );

  return (
    <AdminsPageShell title="Data Ingestion Tool">
      {loadingCountries && <LoadingState />}
      {error && <ErrorState error={error} />}

      {!loadingCountries && !error && (
        <>
          <div className="mb-3">
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="country-select-label">Select a Country</InputLabel>
              <Select
                labelId="country-select-label"
                value={selectedCountry?.isoCode || ''}
                onChange={onCountryChange}
              >
                <MenuItem value="">
                  <em>Select a country</em>
                </MenuItem>
                {countries?.map((country) => (
                  <MenuItem key={country.isoCode} value={country.isoCode}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          {selectedCountry?.sources && (
            <div className="mb-3">
              <FormControl fullWidth>
                <InputLabel id="source-select-label">Select a Source</InputLabel>
                <Select
                  labelId="source-select-label"
                  value={selectedSource?.sourceId || ''}
                  onChange={onSourceChange}
                >
                  <MenuItem value="">
                    <em>Select a source</em>
                  </MenuItem>
                  {selectedCountry.sources.map((source) => (
                    <MenuItem key={source.sourceId} value={source.sourceId}>
                      {source.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          )}

          {runningEvent && (
            <Typography variant="body1" color="error" sx={{ marginTop: 2 }}>
              This country is not available for data ingestion because there is an ongoing process
              for this country. Please wait until the current data ingestion is finished: event{' '}
              {runningEvent?.id}.
            </Typography>
          )}

          {selectedSource && !runningEvent && (
            <SourceDetails country={selectedCountry} source={selectedSource} />
          )}

          {selectedCountry && (
            <div>
              <hr style={styles.divider} />

              <h2>Events for {selectedCountry.name}</h2>
              <EventTable
                country={selectedCountry}
                events={events}
                onFilterChange={handleFilterChange}
                loading={loadingCountryEvents}
              />
            </div>
          )}
        </>
      )}
    </AdminsPageShell>
  );
};
